import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ActivatedRoute } from '@angular/router';
import { map, startWith, switchMap } from 'rxjs/operators';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { AsyncPipe, NgIf } from '@angular/common';
import { Device } from '@shared/models';
import { DeviceFormCardComponent } from '@ep/shared';
import { DeviceFormComponent } from '@app/areas/merchants/forms/device-form/device-form.component';
import { DeviceFormService } from '@app/areas/merchants/services/device-form.service';
import { DevicesApiService } from '@app/areas/merchants/services/devices.api.service';
import { deviceConnectionStatusLabel, deviceConnectionStatusSeverity } from '@app/areas/merchants/utils/device-status';

@Component({
  selector: 'app-device-detail',
  templateUrl: './device-detail.component.html',
  styleUrls: ['./device-detail.component.scss'],
  standalone: true,
  imports: [DeviceFormComponent, DeviceFormCardComponent, AsyncPipe, NgIf],
})
export class DeviceDetailComponent implements OnInit, OnDestroy {
  private readonly deviceFormService = inject(DeviceFormService);
  private readonly devicesApiService = inject(DevicesApiService);
  private readonly messageService = inject(MessageService);
  private readonly route = inject(ActivatedRoute);

  protected device: Device | null = null;
  protected notFound = false;
  protected isSaving = false;

  protected mode$ = this.deviceFormService.mode$;
  protected isSaveDisabled$ = this.deviceFormService.getForm().statusChanges.pipe(
    startWith(this.deviceFormService.getForm().status),
    map((status) => status !== 'VALID')
  );

  private merchantId = 0;
  private subs = new Subscription();

  ngOnInit(): void {
    this.subs.add(
      combineLatest([
        this.route.parent!.parent!.paramMap.pipe(map((params) => Number(params.get('merchantId')))),
        this.route.paramMap.pipe(map((params) => Number(params.get('deviceId')))),
      ]).subscribe(([merchantId, deviceId]) => {
        this.merchantId = merchantId;
        this.loadDevice(merchantId, deviceId);
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private loadDevice(merchantId: number, deviceId: number): void {
    this.subs.add(
      this.devicesApiService.getByIdAndMerchantId(deviceId, merchantId).subscribe((device) => {
        if (!device) {
          this.notFound = true;
          return;
        }

        this.notFound = false;
        this.device = device;
        this.deviceFormService.initializeForView(device);
      })
    );
  }

  protected deviceStatusLabel(device: Device): string {
    return deviceConnectionStatusLabel(device);
  }

  protected deviceStatusSeverity(device: Device): 'success' | 'info' {
    return deviceConnectionStatusSeverity(device);
  }

  protected edit(): void {
    if (this.device) {
      this.deviceFormService.initializeForEdit(this.device);
    }
  }

  protected cancelEdit(): void {
    if (this.device) {
      this.deviceFormService.initializeForView(this.device);
    }
  }

  protected saveDevice(): void {
    const form = this.deviceFormService.getForm();
    if (!form.valid || !this.device) {
      return;
    }

    this.isSaving = true;
    const formValue = form.getRawValue();
    const deviceId = this.device.DeviceId;
    const previousSerialNumber = this.device.SerialNumber;

    this.subs.add(
      this.devicesApiService
        .update(deviceId, this.merchantId, { Name: formValue.Name! })
        .pipe(
          switchMap((device): Observable<Device | null> => {
            if (!device) {
              throw new Error('Something went wrong while updating the device');
            }

            if (formValue.AuthorizationCode) {
              return this.devicesApiService.updateAuthorizationCode(deviceId, this.merchantId, {
                AuthorizationCode: formValue.AuthorizationCode,
              });
            }

            if (formValue.SerialNumber && formValue.SerialNumber !== previousSerialNumber) {
              return this.devicesApiService.updatePreRegistration(deviceId, this.merchantId, {
                SerialNumber: formValue.SerialNumber,
              });
            }

            if (previousSerialNumber && !formValue.SerialNumber) {
              return this.devicesApiService
                .deletePreRegistration(deviceId, this.merchantId)
                .pipe(switchMap(() => this.devicesApiService.getByIdAndMerchantId(deviceId, this.merchantId)));
            }

            return of(device);
          })
        )
        .subscribe({
          next: (device) => {
            this.isSaving = false;
            if (device) {
              this.device = device;
              this.deviceFormService.initializeForView(device);
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'ERROR: Update Device',
                detail: 'Something went wrong while updating the device',
              });
            }
          },
          error: (err) => {
            this.isSaving = false;
            this.messageService.add({
              severity: 'error',
              summary: 'ERROR: Update Device',
              detail: err?.message ?? 'Something went wrong while updating the device',
            });
          },
        })
    );
  }
}
